import React, { useEffect, useState, useRef } from 'react';
import mermaid from 'mermaid';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';

interface MermaidDiagramViewerProps {
  artifact: Artifact;
  mode?: string;
}

export const MermaidDiagramViewer: React.FC<MermaidDiagramViewerProps> = ({ 
  artifact, 
  mode = 'default' 
}) => {
  const { darkMode } = useTheme();
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Initialize mermaid with theme based on dark mode
    mermaid.initialize({
      startOnLoad: true,
      theme: darkMode ? 'dark' : 'default',
      securityLevel: 'loose',
    });
    
    const renderDiagram = async () => {
      try {
        setError(null);
        
        // Extract mermaid code from the artifact
        let mermaidCode = artifact.content.mermaid_code || '';
        
        // Remove markdown code block markers if present
        mermaidCode = mermaidCode.replace(/```mermaid\n|\n```/g, '');
        
        // Generate a unique ID for this diagram
        const id = `mermaid-diagram-${artifact.id}`;
        
        // Render the diagram
        const { svg } = await mermaid.render(id, mermaidCode);
        setSvg(svg);
      } catch (err) {
        console.error('Failed to render Mermaid diagram:', err);
        setError('Failed to render diagram. Please check the syntax.');
      }
    };
    
    renderDiagram();
  }, [artifact, darkMode, mode]);
  
  // Extract the mermaid code for display
  const mermaidCode = artifact.content.mermaid_code?.replace(/```mermaid\n|\n```/g, '') || '';
  
  return (
    <div className="mermaid-diagram-viewer">
      {artifact.content.description && (
        <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {artifact.content.description}
        </p>
      )}
      
      {error ? (
        <div className={`p-4 border rounded ${darkMode ? 'border-red-700 bg-red-900 text-red-300' : 'border-red-300 bg-red-50 text-red-700'}`}>
          {error}
          <pre className={`mt-2 p-2 rounded text-sm overflow-auto ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            {mermaidCode}
          </pre>
        </div>
      ) : (
        <div 
          ref={containerRef}
          className={`mermaid-svg-container p-4 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
          dangerouslySetInnerHTML={{ __html: svg }} 
        />
      )}
      
      {mode !== 'raw' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <details>
            <summary className={`cursor-pointer ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              View Mermaid Code
            </summary>
            <pre className={`mt-2 p-3 rounded text-sm overflow-auto ${
              darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-800'
            }`}>
              {mermaidCode}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};
